import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";

import { Container, ContentContainer } from "@src/core/styles";
import { useSeo } from "@src/core/hooks";
import { APP_ROUTES } from "@src/core/constants";
import {
  ActionButton,
  ArticleBody,
  MonoArea,
  Panel,
  PanelActions,
  PanelBody,
  PanelHeader,
  PanelTitle,
  ResultBox,
  Section,
  SectionTitle,
  SmallButton,
  StatusBadge,
  ToolContainer,
  ToolIntro,
  ToolSubtitle,
  ToolTitle,
  Toolbar,
  ToolbarLeft,
} from "@src/pages/dev-tools-shared";
import {
  ClaimTable,
  DangerBox,
  JwtHighlight,
  JwtPart,
  SignatureVerifyBlock,
} from "./jwt-debugger-page.styles";

type DecodedJwt = {
  headerRaw: string;
  payloadRaw: string;
  signatureRaw: string;
  header: Record<string, unknown>;
  payload: Record<string, unknown>;
};

const EXAMPLE_JWT =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9." +
  "eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ." +
  "SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c";

const base64UrlDecode = (input: string): string => {
  let value = input.replace(/-/g, "+").replace(/_/g, "/");
  while (value.length % 4) value += "=";
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder("utf-8").decode(bytes);
};

const decodeJwt = (token: string): DecodedJwt => {
  const parts = token.trim().split(".");
  if (parts.length !== 3) {
    throw new Error(
      "JWT должен состоять из трёх частей, разделённых точкой: header.payload.signature"
    );
  }
  const [h, p, s] = parts;
  const headerJson = base64UrlDecode(h);
  const payloadJson = base64UrlDecode(p);
  const header = JSON.parse(headerJson);
  const payload = JSON.parse(payloadJson);
  return {
    headerRaw: h,
    payloadRaw: p,
    signatureRaw: s,
    header,
    payload,
  };
};

const base64UrlFromBytes = (bytes: Uint8Array): string => {
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const verifyHmac = async (
  token: string,
  secret: string,
  alg: "HS256" | "HS384" | "HS512"
): Promise<boolean> => {
  const [h, p, s] = token.split(".");
  if (!h || !p || !s) return false;
  const hashName =
    alg === "HS384" ? "SHA-384" : alg === "HS512" ? "SHA-512" : "SHA-256";
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    { name: "HMAC", hash: hashName },
    false,
    ["sign"]
  );
  const signature = await crypto.subtle.sign(
    "HMAC",
    key,
    encoder.encode(`${h}.${p}`)
  );
  const expected = base64UrlFromBytes(new Uint8Array(signature));
  return expected === s;
};

const formatTimestamp = (value: unknown): string | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  const date = new Date(value * 1000);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const STANDARD_CLAIMS: Record<string, string> = {
  iss: "issuer — кто выпустил токен",
  sub: "subject — идентификатор пользователя/сущности",
  aud: "audience — для кого предназначен",
  exp: "expiration — срок истечения",
  nbf: "not before — не валиден до",
  iat: "issued at — когда выпущен",
  jti: "jwt id — уникальный идентификатор токена",
};

export const JwtDebuggerPage: FC = () => {
  const [token, setToken] = useState<string>(EXAMPLE_JWT);
  const [secret, setSecret] = useState<string>("your-256-bit-secret");
  const [verifyResult, setVerifyResult] = useState<
    "idle" | "valid" | "invalid" | "unsupported"
  >("idle");

  const decoded = useMemo(() => {
    try {
      return decodeJwt(token);
    } catch {
      return null;
    }
  }, [token]);

  const parseError = useMemo(() => {
    if (!token.trim()) return "";
    try {
      decodeJwt(token);
      return "";
    } catch (e) {
      return e instanceof Error ? e.message : "Не удалось разобрать JWT";
    }
  }, [token]);

  const alg = decoded?.header.alg as string | undefined;
  const isHmac = alg === "HS256" || alg === "HS384" || alg === "HS512";

  useEffect(() => {
    if (!decoded || !isHmac) {
      setVerifyResult("idle");
      return;
    }
    let cancelled = false;
    verifyHmac(token, secret, alg as "HS256" | "HS384" | "HS512")
      .then((ok) => {
        if (!cancelled) setVerifyResult(ok ? "valid" : "invalid");
      })
      .catch(() => {
        if (!cancelled) setVerifyResult("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [token, secret, alg, decoded, isHmac]);

  const now = Math.floor(Date.now() / 1000);
  const exp = decoded?.payload.exp as number | undefined;
  const nbf = decoded?.payload.nbf as number | undefined;
  const isExpired = typeof exp === "number" && exp < now;
  const isNotYetValid = typeof nbf === "number" && nbf > now;

  const claimsRows = useMemo(() => {
    if (!decoded) return [] as { key: string; value: string; meta?: string }[];
    return Object.entries(decoded.payload).map(([key, raw]) => {
      const meta = STANDARD_CLAIMS[key];
      const ts = formatTimestamp(raw);
      let valueStr: string;
      if (ts) {
        valueStr = `${raw} (${ts})`;
      } else if (typeof raw === "string") {
        valueStr = raw;
      } else {
        valueStr = JSON.stringify(raw);
      }
      return { key, value: valueStr, meta };
    });
  }, [decoded]);

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      enqueueSnackbar("Скопировано", {
        variant: "success",
        preventDuplicate: true,
      });
    } catch {
      enqueueSnackbar("Не удалось скопировать", { variant: "error" });
    }
  };

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "JWT Debugger — декодирование и проверка подписи JWT",
      applicationCategory: "DeveloperApplication",
      operatingSystem: "All",
      offers: { "@type": "Offer", price: "0", priceCurrency: "RUB" },
      description:
        "Онлайн JWT debugger: декодирует header и payload, проверяет подпись HS256/HS384/HS512, отображает срок действия и claims. Работает локально.",
      url: APP_ROUTES.JWT_DEBUGGER,
    }),
    []
  );

  useSeo({
    title: "JWT Debugger — декодировать JWT и проверить подпись онлайн",
    description:
      "Онлайн JWT debugger: декодируйте header и payload, проверьте подпись HS256/384/512, exp и nbf. Токен не отправляется на сервер.",
    canonical: APP_ROUTES.JWT_DEBUGGER,
    jsonLd,
  });

  return (
    <Container>
      <ContentContainer>
        <ToolContainer>
          <ToolIntro>
            <ToolTitle>JWT Debugger</ToolTitle>
            <ToolSubtitle>
              Декодируйте JWT, проверьте подпись (HS256/HS384/HS512), срок
              действия <code>exp</code> и <code>nbf</code>. Токен не уходит на
              сервер — все вычисления происходят в браузере через Web Crypto API.
            </ToolSubtitle>
          </ToolIntro>

          <Toolbar>
            <ToolbarLeft>
              <ActionButton
                variant="outlined"
                onClick={() => setToken(EXAMPLE_JWT)}
              >
                Пример токена
              </ActionButton>
              <ActionButton variant="outlined" onClick={() => setToken("")}>
                Очистить
              </ActionButton>
            </ToolbarLeft>
          </Toolbar>

          <Panel>
            <PanelHeader>
              <PanelTitle>JWT</PanelTitle>
              <PanelActions>
                {decoded && (
                  <>
                    <StatusBadge $variant={isExpired ? "err" : "ok"}>
                      {isExpired ? "Просрочен" : "Валидный формат"}
                    </StatusBadge>
                    {alg && <StatusBadge>{alg}</StatusBadge>}
                  </>
                )}
              </PanelActions>
            </PanelHeader>
            <PanelBody>
              {decoded ? (
                <JwtHighlight>
                  <JwtPart $color="header">{decoded.headerRaw}</JwtPart>
                  <span>.</span>
                  <JwtPart $color="payload">{decoded.payloadRaw}</JwtPart>
                  <span>.</span>
                  <JwtPart $color="signature">{decoded.signatureRaw}</JwtPart>
                </JwtHighlight>
              ) : (
                <ResultBox>
                  {token || "Вставьте JWT для разбора…"}
                </ResultBox>
              )}
              <MonoArea
                multiline
                minRows={3}
                maxRows={8}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="eyJhbGciOi..."
                error={!!parseError}
                helperText={parseError || undefined}
              />
            </PanelBody>
          </Panel>

          {decoded && (
            <>
              <Panel>
                <PanelHeader>
                  <PanelTitle>
                    <JwtPart $color="header">HEADER</JwtPart> · алгоритм и тип
                  </PanelTitle>
                  <PanelActions>
                    <SmallButton
                      variant="outlined"
                      onClick={() =>
                        copy(JSON.stringify(decoded.header, null, 2))
                      }
                    >
                      Копировать
                    </SmallButton>
                  </PanelActions>
                </PanelHeader>
                <ResultBox>{JSON.stringify(decoded.header, null, 2)}</ResultBox>
              </Panel>

              <Panel>
                <PanelHeader>
                  <PanelTitle>
                    <JwtPart $color="payload">PAYLOAD</JwtPart> · claims
                  </PanelTitle>
                  <PanelActions>
                    {isExpired && (
                      <StatusBadge $variant="err">exp просрочен</StatusBadge>
                    )}
                    {isNotYetValid && (
                      <StatusBadge $variant="warn">nbf в будущем</StatusBadge>
                    )}
                    <SmallButton
                      variant="outlined"
                      onClick={() =>
                        copy(JSON.stringify(decoded.payload, null, 2))
                      }
                    >
                      Копировать
                    </SmallButton>
                  </PanelActions>
                </PanelHeader>
                <ResultBox>{JSON.stringify(decoded.payload, null, 2)}</ResultBox>
                {claimsRows.length > 0 && (
                  <ClaimTable>
                    <thead>
                      <tr>
                        <th style={{ width: "20%" }}>Claim</th>
                        <th style={{ width: "45%" }}>Значение</th>
                        <th>Описание</th>
                      </tr>
                    </thead>
                    <tbody>
                      {claimsRows.map((row) => (
                        <tr key={row.key}>
                          <td>
                            <code>{row.key}</code>
                          </td>
                          <td>
                            <code>{row.value}</code>
                          </td>
                          <td>{row.meta || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </ClaimTable>
                )}
              </Panel>

              <Panel>
                <PanelHeader>
                  <PanelTitle>
                    <JwtPart $color="signature">SIGNATURE</JwtPart> · проверка
                  </PanelTitle>
                  <PanelActions>
                    {verifyResult === "valid" && (
                      <StatusBadge $variant="ok">Подпись валидна</StatusBadge>
                    )}
                    {verifyResult === "invalid" && (
                      <StatusBadge $variant="err">Подпись не совпадает</StatusBadge>
                    )}
                    {!isHmac && alg && (
                      <StatusBadge $variant="warn">
                        {alg} не поддерживается в браузере
                      </StatusBadge>
                    )}
                  </PanelActions>
                </PanelHeader>
                <SignatureVerifyBlock>
                  {isHmac ? (
                    <>
                      <MonoArea
                        value={secret}
                        onChange={(e) => setSecret(e.target.value)}
                        placeholder="Секрет для HMAC"
                      />
                      <ArticleBody>
                        <p>
                          Подпись вычисляется как{" "}
                          <code>
                            HMAC-{alg?.slice(2)}(base64url(header) + "." +
                            base64url(payload), secret)
                          </code>
                          . Введите тот же секрет, которым подписывался токен на
                          сервере.
                        </p>
                      </ArticleBody>
                    </>
                  ) : alg === "none" ? (
                    <DangerBox>
                      Алгоритм <code>none</code> — небезопасно. Никогда не
                      принимайте такие токены на бэкенде.
                    </DangerBox>
                  ) : (
                    <ArticleBody>
                      <p>
                        Для алгоритмов RS256/ES256 нужна публичная часть ключа
                        (PEM). Проверка подписи асимметричных алгоритмов в этом
                        инструменте пока не поддерживается — используйте
                        серверную библиотеку вашего языка.
                      </p>
                    </ArticleBody>
                  )}
                </SignatureVerifyBlock>
              </Panel>
            </>
          )}

          <Section>
            <SectionTitle>Как устроен JWT</SectionTitle>
            <ArticleBody>
              <p>
                <strong>JWT (JSON Web Token)</strong> — это три Base64URL‑части,
                разделённые точками:{" "}
                <code>
                  <JwtPart $color="header">header</JwtPart>.<JwtPart $color="payload">payload</JwtPart>.<JwtPart $color="signature">signature</JwtPart>
                </code>
                . Header и payload — это JSON, закодированный в{" "}
                <a href={APP_ROUTES.BASE64}>Base64URL</a>. Signature — подпись
                первых двух частей секретом или приватным ключом.
              </p>
              <h3>Стандартные claim'ы (RFC 7519)</h3>
              <ul>
                <li>
                  <code>iss</code> — кто выпустил токен.
                </li>
                <li>
                  <code>sub</code> — идентификатор пользователя.
                </li>
                <li>
                  <code>aud</code> — для какой системы предназначен токен.
                </li>
                <li>
                  <code>exp</code> — до какого времени (Unix‑секунды) токен
                  валиден.
                </li>
                <li>
                  <code>nbf</code> — с какого момента токен можно принимать.
                </li>
                <li>
                  <code>iat</code> — момент выпуска.
                </li>
                <li>
                  <code>jti</code> — уникальный ID токена (например{" "}
                  <a href={APP_ROUTES.UUID_GENERATOR}>UUID</a>), полезно для
                  revoke‑листов.
                </li>
              </ul>
              <h3>Частые ошибки</h3>
              <ul>
                <li>
                  Принимать <code>alg: none</code> — классическая уязвимость. В
                  продакшн‑коде явно укажите ожидаемые алгоритмы в библиотеке.
                </li>
                <li>
                  Не проверять <code>exp</code> — токен, который «живёт вечно»,
                  невозможно отозвать.
                </li>
                <li>
                  Класть секретные данные в payload — он не шифруется, только
                  кодируется Base64URL.
                </li>
              </ul>
              <p>
                Нужно эмулировать ответ авторизационного сервиса в тестах?
                Поднимите мок‑эндпоинт <code>/auth/token</code> в{" "}
                <a href={APP_ROUTES.DASHBOARD}>LightBoxAPI</a> и возвращайте заранее
                подготовленные JWT — удобно прогонять фронтенд или интеграционные
                тесты без реального бэкенда.
              </p>
            </ArticleBody>
          </Section>
        </ToolContainer>
      </ContentContainer>
    </Container>
  );
};
